import React, { useState, useEffect, useRef, useCallback } from 'react';
import RichTextContent from '../components/RichTextContent';
import { useParams, Link, useLocation } from 'react-router-dom';
import { ArrowRight, ExternalLink, ChevronRight, ChevronLeft, Loader2, Calendar, Search, Pin } from 'lucide-react';
import { brandsApi, meshNewsApi, catalogueApi } from '../services/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { BrandLightingTab } from '../components/catalogue/BrandLightingTab';

const BrandPage = () => {
  const { brandId } = useParams();
  const location = useLocation();
  const [brand, setBrand] = useState(null);
  const [allBrands, setAllBrands] = useState([]);
  const [brandNews, setBrandNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const catalogueGridRef = useRef(null);

  // Catalogue state — restore page from URL param
  const searchParams = new URLSearchParams(location.search);
  const initialPage = parseInt(searchParams.get('catPage') || '1', 10);
  const [catalogue, setCatalogue] = useState({ products: [], total: 0, totalPages: 0, categories: [] });
  const [catPage, setCatPage] = useState(initialPage);
  const [catCategory, setCatCategory] = useState('');
  const [catSearch, setCatSearch] = useState('');
  const [catLoading, setCatLoading] = useState(false);

  // Scroll to top of catalogue grid when page changes
  const scrollToGrid = useCallback(() => {
    if (catalogueGridRef.current) {
      catalogueGridRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [brandRes, brandsRes, newsRes] = await Promise.all([
          brandsApi.get(brandId),
          brandsApi.getAll(),
          meshNewsApi.getByBrand(brandId),
        ]);
        setBrand(brandRes.data);
        setAllBrands(brandsRes.data);
        setBrandNews(newsRes.data);
      } catch (error) {
        console.error('Error fetching brand:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [brandId]);

  // Fetch catalogue when brand has tabCatalogue enabled
  useEffect(() => {
    if (!brand?.tabCatalogue) return;
    const fetchCatalogue = async () => {
      setCatLoading(true);
      try {
        const res = await catalogueApi.getAll(brandId, { page: catPage, limit: 24, category: catCategory, search: catSearch });
        setCatalogue(res.data);
      } catch (error) {
        console.error('Error fetching catalogue:', error);
      } finally {
        setCatLoading(false);
      }
    };
    fetchCatalogue();
  }, [brand?.tabCatalogue, brandId, catPage, catCategory, catSearch]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!brand) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-medium text-gray-900 mb-4">Brand not found</h1>
          <Link to="/" className="text-gray-600 hover:text-gray-900">
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  const showVisitWebsite = brand.showVisitWebsite !== false && brand.website;
  const showBestOf = brand.tabBestOf !== false;
  const showCatalogue = brand.tabCatalogue === true;
  const defaultTab = showCatalogue ? 'catalogue' : 'about';
  const familyMode = brand.catalogueMode === 'families';

  return (
    <div className="bg-white" data-testid="brand-page">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center">
        <div className="absolute inset-0 bg-gray-900">
          <div
            className="absolute inset-0 bg-cover bg-center opacity-50"
            style={{ backgroundImage: `url(${brand.heroImage})` }}
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm text-gray-300 mb-8">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-white">{brand.name}</span>
          </nav>

          <div className="max-w-3xl">
            {/* Brand Logo */}
            {brand.logo && (
              <div className="mb-6">
                <img 
                  src={brand.logo} 
                  alt={`${brand.name} logo`} 
                  className="max-w-[120px] h-auto brightness-0 invert opacity-90"
                  data-testid="brand-logo"
                />
              </div>
            )}
            <span className="text-sm uppercase tracking-wider text-gray-300 mb-4 block">
              {brand.country} • {brand.specialty}
            </span>
            <h1 className="text-5xl md:text-6xl font-light text-white mb-6">
              {brand.name}
            </h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              {brand.descriptionShort || brand.about?.substring(0, 200)}
              {!brand.descriptionShort && brand.about?.length > 200 ? '...' : ''}
            </p>
            {showVisitWebsite && (
              <a
                href={brand.website}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 border border-white/30 text-white font-medium rounded-sm hover:bg-white/10 transition-colors"
                data-testid="visit-website-btn"
              >
                Visit Website
                <ExternalLink className="ml-2 h-4 w-4" />
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Tabs Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs defaultValue={defaultTab} className="w-full">
            <TabsList className="w-full justify-start overflow-x-auto overflow-y-hidden flex-nowrap border-b border-gray-200 bg-transparent rounded-none h-auto p-0 mb-12">
              {showCatalogue && (
                <TabsTrigger
                  value="catalogue"
                  className="px-4 sm:px-6 py-4 text-sm font-medium whitespace-nowrap rounded-none border-b-2 border-transparent data-[state=active]:border-gray-900 data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                  data-testid="tab-catalogue"
                >
                  {familyMode ? `Lighting (${catalogue.total})` : `Catalogue (${catalogue.total})`}
                </TabsTrigger>
              )}
              <TabsTrigger
                value="about"
                className="px-4 sm:px-6 py-4 text-sm font-medium whitespace-nowrap rounded-none border-b-2 border-transparent data-[state=active]:border-gray-900 data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                data-testid="tab-about"
              >
                About
              </TabsTrigger>
              <TabsTrigger
                value="news"
                className="px-4 sm:px-6 py-4 text-sm font-medium whitespace-nowrap rounded-none border-b-2 border-transparent data-[state=active]:border-gray-900 data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                data-testid="tab-news"
              >
                News
              </TabsTrigger>
              {showBestOf && (
                <TabsTrigger
                  value="best-of"
                  className="px-4 sm:px-6 py-4 text-sm font-medium whitespace-nowrap rounded-none border-b-2 border-transparent data-[state=active]:border-gray-900 data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                  data-testid="tab-best-of"
                >
                  Best Of
                </TabsTrigger>
              )}
              <TabsTrigger
                value="projects"
                className="px-4 sm:px-6 py-4 text-sm font-medium whitespace-nowrap rounded-none border-b-2 border-transparent data-[state=active]:border-gray-900 data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                data-testid="tab-projects"
              >
                Project Gallery
              </TabsTrigger>
            </TabsList>

            {/* Catalogue Tab */}
            {showCatalogue && (
              <TabsContent value="catalogue" className="mt-0">
                {familyMode ? <BrandLightingTab brandId={brandId} brandName={brand.name} /> : (
                <div className="mb-8" ref={catalogueGridRef}>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                    <h2 className="text-2xl font-medium text-gray-900" data-testid="catalogue-heading">
                      {brand.name} Catalogue
                    </h2>
                    <div className="flex items-center gap-3">
                      {/* Search */}
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          value={catSearch}
                          onChange={(e) => { setCatSearch(e.target.value); setCatPage(1); }}
                          placeholder="Search name or code..."
                          className="text-sm border border-gray-200 rounded-md pl-9 pr-3 py-1.5 bg-white w-52 focus:outline-none focus:ring-1 focus:ring-gray-400"
                          data-testid="catalogue-search"
                        />
                      </div>
                      {/* Category filter */}
                      {catalogue.categories?.length > 1 && (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500">Filter:</span>
                        <select
                          value={catCategory}
                          onChange={(e) => { setCatCategory(e.target.value); setCatPage(1); }}
                          className="text-sm border border-gray-200 rounded-md px-3 py-1.5 bg-white"
                          data-testid="category-filter"
                        >
                          <option value="">All ({catalogue.total})</option>
                          {catalogue.categories.map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    </div>
                  </div>

                  {catLoading ? (
                    <div className="flex justify-center py-16">
                      <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                        {catalogue.products.map((product) => (
                          <Link
                            key={product.slug}
                            to={`/brand/${brandId}/products/${product.slug}?page=${catPage}`}
                            className="group relative"
                            data-testid={`catalogue-product-${product.slug}`}
                          >
                            {product.pinned && (
                              <span className="absolute top-1.5 right-1.5 z-10 bg-amber-400 text-white rounded-full p-1" title="Pinned">
                                <Pin className="h-3 w-3" />
                              </span>
                            )}
                            <div className="aspect-[5/7] bg-gray-50 rounded-lg overflow-hidden mb-2">
                              <img
                                src={product.mainImage}
                                alt={product.name}
                                className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105"
                                loading="lazy"
                              />
                            </div>
                            <p className="text-xs text-gray-500 font-mono">{product.code}</p>
                            <p className="text-sm font-medium text-gray-900 group-hover:text-gray-600 transition-colors truncate">
                              "{product.name}"
                            </p>
                          </Link>
                        ))}
                      </div>

                      {/* Pagination */}
                      {catalogue.totalPages > 1 && (
                        <div className="flex items-center justify-center gap-2 mt-10" data-testid="catalogue-pagination">
                          <button
                            onClick={() => { setCatPage(p => Math.max(1, p - 1)); setTimeout(scrollToGrid, 100); }}
                            disabled={catPage === 1}
                            className="p-2 rounded-md border border-gray-200 disabled:opacity-30 hover:bg-gray-50 transition-colors"
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </button>
                          {Array.from({ length: catalogue.totalPages }, (_, i) => i + 1).map(p => (
                            <button
                              key={p}
                              onClick={() => { setCatPage(p); setTimeout(scrollToGrid, 100); }}
                              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                                p === catPage ? 'bg-gray-900 text-white' : 'border border-gray-200 hover:bg-gray-50'
                              }`}
                            >
                              {p}
                            </button>
                          ))}
                          <button
                            onClick={() => { setCatPage(p => Math.min(catalogue.totalPages, p + 1)); setTimeout(scrollToGrid, 100); }}
                            disabled={catPage === catalogue.totalPages}
                            className="p-2 rounded-md border border-gray-200 disabled:opacity-30 hover:bg-gray-50 transition-colors"
                          >
                            <ChevronRight className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
                )}
              </TabsContent>
            )}

            {/* About Tab */}
            <TabsContent value="about" className="mt-0">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                <div className="lg:col-span-2">
                  <h2 className="text-2xl font-medium text-gray-900 mb-6">About {brand.name}</h2>
                  <p className="text-gray-600 leading-relaxed mb-8 whitespace-pre-line">{brand.about}</p>
                  
                  {brand.features?.length > 0 && (
                    <>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Key Features</h3>
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {brand.features.map((feature) => (
                          <li key={feature} className="flex items-center text-gray-600">
                            <span className="w-2 h-2 bg-gray-900 rounded-full mr-3 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
                <div className="bg-gray-50 rounded-lg p-8">
                  <h3 className="text-lg font-medium text-gray-900 mb-6">Brand Details</h3>
                  <dl className="space-y-4">
                    <div>
                      <dt className="text-sm text-gray-500">Country</dt>
                      <dd className="text-gray-900 font-medium">{brand.country}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-500">Specialty</dt>
                      <dd className="text-gray-900 font-medium">{brand.specialty}</dd>
                    </div>
                    {brand.tagline && (
                      <div>
                        <dt className="text-sm text-gray-500">Tagline</dt>
                        <dd className="text-gray-900 font-medium italic">{brand.tagline}</dd>
                      </div>
                    )}
                    {showVisitWebsite && (
                      <div>
                        <dt className="text-sm text-gray-500">Website</dt>
                        <dd>
                          <a
                            href={brand.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gray-900 font-medium hover:underline inline-flex items-center"
                          >
                            Visit Site
                            <ExternalLink className="ml-1 h-3 w-3" />
                          </a>
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>
              </div>
            </TabsContent>

            {/* News Tab */}
            <TabsContent value="news" className="mt-0">
              <h2 className="text-2xl font-medium text-gray-900 mb-8">{brand.name} News</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {brandNews.map((item) => (
                  <Link
                    key={item.id}
                    to={`/news/${item.id}`}
                    className="group bg-white border border-gray-100 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                          {item.category}
                        </span>
                        <span className="text-xs text-gray-500 flex items-center">
                          <Calendar className="h-3 w-3 mr-1" />
                          {item.date}
                        </span>
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2 group-hover:text-gray-700 transition-colors">
                        {item.title}
                      </h3>
                      <RichTextContent preview content={item.excerpt} className="text-gray-600 text-sm line-clamp-2 mb-3 [&_*]:my-0 [&_p]:text-inherit [&_*]:text-inherit" testId={`brand-news-excerpt-${item.id}`} />
                      <span className="inline-flex items-center text-sm text-gray-900 font-medium group-hover:underline">
                        Read Article
                        <ChevronRight className="ml-1 h-3 w-3" />
                      </span>
                    </div>
                  </Link>
                ))}
                {brandNews.length === 0 && (
                  <p className="text-gray-500 col-span-3 text-center py-12">No news articles tagged for this brand yet</p>
                )}
              </div>
            </TabsContent>

            {/* Best Of Tab */}
            {showBestOf && (
              <TabsContent value="best-of" className="mt-0">
                <h2 className="text-2xl font-medium text-gray-900 mb-8">Best of {brand.name}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {brand.products?.map((item) => (
                    <div key={item.id} className="group">
                      <div className="aspect-square overflow-hidden rounded-lg mb-4">
                        {item.link ? (
                          <a href={item.link} target="_blank" rel="noopener noreferrer" className="block w-full h-full">
                            <img
                              src={item.image}
                              alt={item.name}
                              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 cursor-pointer"
                            />
                          </a>
                        ) : (
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        )}
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 mb-1">{item.name}</h3>
                      <p className="text-sm text-gray-600 mb-3">{item.description}</p>
                      {item.link && (
                        <a
                          href={item.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-sm text-gray-900 font-medium hover:underline"
                        >
                          {item.linkText || 'View Product'}
                          <ExternalLink className="ml-1 h-3 w-3" />
                        </a>
                      )}
                    </div>
                  ))}
                  {!brand.products?.length && (
                    <p className="text-gray-500 col-span-4 text-center py-12">No products available</p>
                  )}
                </div>
              </TabsContent>
            )}

            {/* Projects Tab */}
            <TabsContent value="projects" className="mt-0">
              <div className="text-center py-16">
                <h2 className="text-2xl font-medium text-gray-900 mb-4">Project Gallery</h2>
                <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
                  Explore {brand.name}'s complete portfolio of lighting projects, including detailed specifications, 
                  photography, and case studies.
                </p>
                {showVisitWebsite && (
                  <a
                    href={brand.projectsPageUrl || (brand.website ? `${brand.website.replace(/\/$/, '')}/projects` : brand.website)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-8 py-4 bg-gray-900 text-white font-medium rounded-sm hover:bg-gray-800 transition-colors"
                    data-testid="view-projects-btn"
                  >
                    View Projects on {brand.name} Website
                    <ExternalLink className="ml-2 h-5 w-5" />
                  </a>
                )}
                {!showVisitWebsite && (
                  <p className="text-gray-500">
                    Contact us at <a href="mailto:info@meshlighting.co.uk" className="underline">info@meshlighting.co.uk</a> for project references.
                  </p>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Other Brands Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-medium text-gray-900 mb-8">Explore Other Brands</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {allBrands
              .filter((b) => b.id !== brand.id)
              .map((otherBrand) => (
                <Link
                  key={otherBrand.id}
                  to={`/brand/${otherBrand.id}`}
                  className="group relative overflow-hidden rounded-lg aspect-[4/3]"
                >
                  <img
                    src={otherBrand.heroImage}
                    alt={otherBrand.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/50 group-hover:bg-black/40 transition-colors" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-white font-medium text-lg">{otherBrand.name}</span>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default BrandPage;
